import axios from 'axios';

// The campaign-1 backend (bks.atlas-innovate.store) no longer exists and its
// source was never committed, so the app talks to the InterServer handlers
// directly: one endpoint runs SQL, the other stores bill photos.
export const SQL_API = 'https://mas.phvtech.com/api/Master/sp';
export const FILE_API = 'https://folder.phvtech.com/api/directory';
export const FILE_FOLDER = 'innovate2';
const DB = '[phvtech_Main]';

// Campaign 2 is Atlas-only. Kept as a constant so a future brand only has to
// change here and in the QR links (?b=).
export const BRAND_ATLAS = 2;

// Campaign 2 prizes. No reload this time.
export const GIFT_BAG = 1;
export const GIFT_VOUCHER = 2;
export const GIFT_NAMES = { [GIFT_BAG]: 'Cross Body Bag', [GIFT_VOUCHER]: 'Cargills Voucher' };

/**
 * Escape a value for inline SQL. The handler takes a raw statement, so there
 * is no parameter binding available -- every user-supplied value must go
 * through here or a quote in a shop/customer name breaks the statement.
 */
export function sqlStr(v) {
  if (v === null || v === undefined || v === '') return 'NULL';
  return `N'${String(v).replace(/'/g, "''")}'`;
}

/** Escape a value that must be an integer; anything else becomes NULL. */
export function sqlInt(v) {
  if (v === null || v === undefined || v === '') return 'NULL';
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? String(n) : 'NULL';
}

/** Escape a value that must be a number; anything else becomes 0. */
export function sqlNum(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? String(n) : '0';
}

/** Run a statement against phvtech_Main and return the first result set. */
export async function runSql(sql) {
  const res = await axios.post(SQL_API, { SysID: `USE ${DB}; ${sql}` });
  return Array.isArray(res.data) ? res.data : [];
}

/** Run a statement and return just the first row (or null). */
export async function runSqlRow(sql) {
  const rows = await runSql(sql);
  return rows.length ? rows[0] : null;
}

// ---------------------------------------------------------------- consumer

/**
 * Decide win/lose for a shop, and which prize a win takes.
 * Returns { win, giftType } -- giftType is GIFT_BAG / GIFT_VOUCHER on a win
 * and null on a loss. The prize alternates bag, voucher, bag ... per shop
 * per day; the proc owns that so the rule can change without a redeploy.
 */
export async function checkWin(shopId, b = BRAND_ATLAS) {
  const row = await runSqlRow(
    `EXEC dbo.inv2_gift @shop_id = ${sqlInt(shopId)}, @b = ${sqlInt(b)}`
  );
  // g = 0 means win, matching campaign 1's contract
  const win = !!row && row.g < 1;
  return { win, giftType: win && row.gift_type ? Number(row.gift_type) : null };
}

/**
 * True when this phone number has already entered.
 *
 * The handler serialises column names with the first character lower-cased
 * (Dialog_Count -> dialog_Count), so `IsRegistered` arrives as `isRegistered`.
 * Both spellings are accepted: reading only the SQL spelling yields undefined
 * and would wave every duplicate through.
 */
export async function isRegistered(phone) {
  const row = await runSqlRow(`EXEC dbo.inv2_tp_sp @tp = ${sqlStr(phone)}`);
  if (!row) return false;
  const flag = row.isRegistered !== undefined ? row.isRegistered : row.IsRegistered;
  return Number(flag) === 1;
}

/**
 * Shrink a phone photo before upload. Cameras hand us 12MP / 1-2MB JPEGs;
 * a bill only has to be legible, and at the outlet this is going over mobile
 * data while the customer waits, so the full-size original is the single
 * slowest part of registering. Typically 2MB -> ~150-250KB.
 *
 * Falls back to the original file if anything about decoding fails -- a
 * missing photo is far worse than a large one.
 */
export async function compressImage(file, maxDim = 1600, quality = 0.8) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;

  try {
    // imageOrientation honours the EXIF rotation phones record, otherwise
    // portrait bills come out sideways.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size < 400 * 1024) {
      bitmap.close();
      return file; // already small enough to leave alone
    }

    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close();

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    if (!blob || blob.size >= file.size) return file; // no gain, keep original

    return new File([blob], 'bill.jpg', { type: 'image/jpeg' });
  } catch (err) {
    console.warn('Bill image compression failed, uploading original:', err);
    return file;
  }
}

/** Upload the bill photo (compressed) and return its public URL. */
export async function uploadBillImage(file, phone) {
  const toSend = await compressImage(file);

  const ext = (toSend.name && toSend.name.includes('.') ? toSend.name.split('.').pop() : 'jpg')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  const safePhone = String(phone || 'na').replace(/[^0-9]/g, '');
  const fileName = `${Date.now()}_${safePhone}.${ext || 'jpg'}`;

  const form = new FormData();
  form.append('files', toSend, fileName);

  await axios.post(`${FILE_API}/${FILE_FOLDER}/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  return `${FILE_API}/${FILE_FOLDER}/files/${fileName}`;
}

/** Register an entrant. Uploads the bill photo first, then inserts the row. */
export async function register({ name, phone, billImage, shopId, b = BRAND_ATLAS }) {
  let billUrl = null;
  if (billImage) billUrl = await uploadBillImage(billImage, phone);

  const row = await runSqlRow(
    `EXEC dbo.inv2_register @name = ${sqlStr(name)}, @tp = ${sqlStr(phone)}, ` +
    `@shop_id = ${sqlInt(shopId)}, @b = ${sqlInt(b)}, @bill_img = ${sqlStr(billUrl)}`
  );
  return { userId: row ? row.userId : null, billUrl };
}

/** Mark an entry as a winner, recording which prize was revealed. */
export async function setWin(id, giftType = null) {
  if (!id) return false;
  const gift = giftType ? sqlInt(giftType) : 'NULL';
  const row = await runSqlRow(
    `EXEC dbo.inv2_set_win @id = ${sqlInt(id)}, @gift_type = ${gift}`
  );
  return !!row && row.updated > 0;
}

// ------------------------------------------------------------------- admin

/** All entries, newest first, joined to shop details. */
export function getScratches() {
  return runSql('EXEC dbo.inv2_scratches');
}

/** Approve (1) or decline (2) a bill photo. */
export function setApproved(id, a) {
  return runSql(`EXEC dbo.inv2_set_approve @id = ${sqlInt(id)}, @a = ${sqlInt(a)}`);
}

/** Mark a gift / reload as issued. */
export function setGift(id, r = 1) {
  return runSql(`EXEC dbo.inv2_set_gift @id = ${sqlInt(id)}, @r = ${sqlInt(r)}`);
}

/** Per-shop win/lose counts for one day. */
export function getDailyStats(date, b = null) {
  const brand = b === null ? 'NULL' : sqlInt(b);
  return runSql(`EXEC dbo.inv2_gsum @date = ${sqlStr(date)}, @b = ${brand}`);
}

/** Reload cost totals for approved winners. */
export function getReloadTotals() {
  return runSql('EXEC dbo.inv2_reload');
}

// ------------------------------------------------------------ shops / promoter

/** Active outlets: { shopId, shopName, territory, region, teamName, ... }. */
export function getOutlets() {
  return runSql('EXEC dbo.inv2_outlets');
}

/** Submit a promoter's daily sales entry. */
export function addPromoterSales(p) {
  return runSql(
    'EXEC dbo.inv2_promoter_add ' +
    `@teamName = ${sqlStr(p.teamName)}, @shopId = ${sqlInt(p.shopId)}, ` +
    `@shopName = ${sqlStr(p.shopName)}, @game = ${sqlStr(p.game)}, ` +
    `@billingDate = ${sqlStr(p.billingDate)}, @timeSlot = ${sqlStr(p.timeSlot)}, ` +
    `@exercise = ${sqlInt(p.exercise) === 'NULL' ? 0 : sqlInt(p.exercise)}, ` +
    `@b5 = ${sqlInt(p.b5) === 'NULL' ? 0 : sqlInt(p.b5)}, ` +
    `@cr = ${sqlInt(p.cr) === 'NULL' ? 0 : sqlInt(p.cr)}, ` +
    `@others = ${sqlInt(p.others) === 'NULL' ? 0 : sqlInt(p.others)}, ` +
    `@billCount = ${sqlInt(p.billCount) === 'NULL' ? 0 : sqlInt(p.billCount)}, ` +
    `@amount = ${sqlNum(p.amount)}`
  );
}

/** Pivoted promoter sales report for one day. */
export function getPromoterReport(date) {
  return runSql(`EXEC dbo.inv2_promoter_sales_sp @dat = ${sqlStr(date)}`);
}
